import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ButtonContainer,
  EditButton,
  DeleteButton,
} from './style'
import { BlurBackground } from '../style'
import cross from '../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../assets/edit-pen-white.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`

interface ViewCollectionModalProps {
  collectionId: number
  onClose: () => void
}

interface CollectionDetailItem {
  label: string
  value: string
}

interface FullCollectionData {
  general: CollectionDetailItem[]
  families: CollectionDetailItem[]
  grades: CollectionDetailItem[]
  groups: CollectionDetailItem[]
  fabrics: CollectionDetailItem[]
  trimmings: CollectionDetailItem[]
}

const ViewCollectionModal: React.FC<ViewCollectionModalProps> = ({
  collectionId,
  onClose,
}) => {
  const navigate = useNavigate()

  const [collectionData, setCollectionData] =
    useState<FullCollectionData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchCollectionData = async () => {
      if (!collectionId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(
          `${API_BASE_URL}/collections/${collectionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        const data = response.data

        const mappedGeneral: CollectionDetailItem[] = [
          { label: 'ID', value: data.id?.toString() || 'N/A' },
          { label: 'Nome da Coleção', value: data.name || 'N/A' },
        ]

        let mappedFamilies: CollectionDetailItem[] = []
        try {
          const familiesResponse = await axios.get(
            `${API_BASE_URL}/collections/${collectionId}/families`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          )
          const familiesData = familiesResponse.data
          const familyNames = Array.isArray(familiesData) && familiesData.length > 0
            ? familiesData.map((f: any) => f.name).join(', ')
            : 'Nenhuma família cadastrada'
          mappedFamilies = [{ label: 'Famílias', value: familyNames }]
        } catch (err) {
          mappedFamilies = [{ label: 'Famílias', value: 'Nenhuma família encontrada' }]
        }

        const gradeNames = (data.GradeInCollection || [])
          .map((g: any) => g.Grade?.name)
          .filter(Boolean)
        const mappedGrades: CollectionDetailItem[] = [
          { label: 'Grades', value: gradeNames.length > 0 ? gradeNames.join(', ') : 'Nenhuma grade' },
        ]

        const groupNames = (data.GroupInCollection || [])
          .map((g: any) => g.Group?.name)
          .filter(Boolean)
        const mappedGroups: CollectionDetailItem[] = [
          { label: 'Grupos', value: groupNames.length > 0 ? groupNames.join(', ') : 'Nenhum grupo' },
        ]

        const fabricNames = (data.CollectionMaterial || [])
          .map((cm: any) => cm.Material?.name)
          .filter(Boolean)
        const mappedFabrics: CollectionDetailItem[] = [
          { label: 'Tecidos', value: fabricNames.length > 0 ? fabricNames.join(', ') : 'Nenhum tecido' },
        ]

        const trimmingNames = (data.CollectionMaterial || [])
          .flatMap((cm: any) => (cm.CollectionMaterialResources || []).map((r: any) => r.Resource?.name))
          .filter(Boolean)
        const uniqueTrimmings = [...new Set(trimmingNames)]
        const mappedTrimmings: CollectionDetailItem[] = [
          { label: 'Aviamentos', value: uniqueTrimmings.length > 0 ? uniqueTrimmings.join(', ') : 'Nenhum aviamento' },
        ]

        setCollectionData({
          general: mappedGeneral,
          families: mappedFamilies,
          grades: mappedGrades,
          groups: mappedGroups,
          fabrics: mappedFabrics,
          trimmings: mappedTrimmings,
        })
        setLoading(false)
      } catch (err: any) {
        console.error('Erro ao buscar detalhes da coleção:', err)
        setError(
          err.response?.data?.message ||
            'Erro ao carregar dados da coleção. Tente novamente.',
        )
        setLoading(false)
      }
    }

    fetchCollectionData()
  }, [collectionId])

  const handleClose = () => {
    onClose()
  }

  const handleEditCollection = () => {
    navigate(`/panel/collections/edit/${collectionId}`)
    onClose()
  }

  const handleDeleteCollection = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta coleção?')) return

    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada. Faça login novamente.')
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/collections/${collectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      alert('Coleção excluída com sucesso!')
      onClose()
      window.location.reload()
    } catch (err: any) {
      console.error('Erro ao excluir coleção:', err)
      alert(
        err.response?.data?.message ||
          'Erro ao excluir coleção. Tente novamente.',
      )
    }
  }

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados da coleção...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Carregando informações da coleção...</p>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
              <button onClick={handleClose}>Fechar</button>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!collectionData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Coleção não encontrada</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Nenhum dado encontrado para a coleção com ID: {collectionId}.
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const collectionNameItem = collectionData.general.find(
    (item) => item.label === 'Nome da Coleção',
  )
  const displayName = collectionNameItem
    ? collectionNameItem.value
    : `Coleção #${collectionId}`

  return (
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Coleção: {displayName}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {collectionData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Famílias</SectionHeader>
            <DetailGrid>
              {collectionData.families.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Grades</SectionHeader>
            <DetailGrid>
              {collectionData.grades.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Grupos</SectionHeader>
            <DetailGrid>
              {collectionData.groups.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Tecidos</SectionHeader>
            <DetailGrid>
              {collectionData.fabrics.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Aviamentos</SectionHeader>
            <DetailGrid>
              {collectionData.trimmings.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>
        </ContentContainer>

        <ButtonContainer>
          <DeleteButton onClick={handleDeleteCollection}>
            <img src={trash} alt='Excluir' />
            Excluir coleção
          </DeleteButton>
          <EditButton style={{ marginLeft: '1rem' }} onClick={handleEditCollection}>
            <img src={editIcon} alt='Editar' />
            Editar coleção
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
  )
}

export default ViewCollectionModal